"use strict"

import { Data } from '@/lib/core/components/data/data'
import { ImoveisDM } from '@/lib/core/imoveis/imoveis'
import { LotesDM } from '@/lib/core/public/lotes'
import { ITBIsDM, ITBI } from '@/lib/core/public/itbis'
// Form
import { FieldText } from '@/lib/core/components/data/form/FieldText'
import { multilines } from '@/lib/core/components/data/form/multilines'
import { FieldMap } from '@/lib/core/components/data/form/FieldMap'
// Lib
import * as jsf from '@/lib/js_functions'
// Mapas
import { irParaMapaButton } from '@/lib/core/maps/mapsFormShared'

export class ImoveisForm extends Data {
  #lotes
  #itbis
  #lote
  #unidade
  constructor({ Logic }) {
    super({ module: 'data', dataId: 'imoveis', Logic, originClass: ImoveisForm })
  }
  build = async ({ register, fields, companySpecialFields, notStart }) => {
    const specialFields = {
      irParaMapaButton,
      negociacoes: NegociacoesMultilines,
      ['negociacoes.valor']: NegociacoesValorText,
      ['negociacoes.valorporm2']: NegociacoesValorporm2Text,
      poligono: ImovelFieldMap,
      anuncios: AnunciosMultilines
    }
    Object.assign(specialFields, companySpecialFields)
    this.form.build({ register, fields, specialFields })
    notStart || await this.start({ register })
    return this
  }
  async start({ register }) {
    this.#lote = register.loteClass
    this.#unidade = register.unidadeClass
    delete register.loteClass
    delete register.unidadeClass
    const isNew = !register._id
    const notHasLotes = !register.lotes?.[0]
    const notHasPoligono = !register.poligono || register.poligono.length == 0
    if (notHasPoligono) { this.form.poligono.startAction = () => this.form?.poligono?.centerMap() }
    else { this.form.poligono.startAction = () => this.form.poligono.insertImovelOfRegister() }
    if (isNew) {
      if (notHasLotes) return
      await this.#getITBIInfos({ register })
      this.#fillInfosOnRegister({ register })
      this.#fillPublicInfosOnRegister({ register })
    }
  }
  async #getITBIInfos({ register }) { // eslint-disable-line
    const sql = this.#lote?.sql
    this.#itbis = await (new ITBIsDM({})).getITBIsOfLote({ sql, project: ITBI.projectImoveisForm })
  }
  #fillInfosOnRegister({ register }) { // eslint-disable-line
    const lote = register.lotes?.[0]
    const loteFields = [
      'matricula',
      'area',
      'valor',
      'obs'
    ]
    loteFields.forEach(field => { register[field] = lote[field] })
    this.form.negociacoes.fillInfos({ register })
  }
  #fillPublicInfosOnRegister({ register }) { // eslint-disable-line
    this.#fillIPTUsInfos({ register })
    this.#fillITBIsInfos({ register })
    this.form.poligono?.setInFieldMap()
  }
  async #fillIPTUsInfos({ register }) { // eslint-disable-line
    const iptuFields = [
      "contribuinte",
      "codlog",
      "cep",
      "logradouro",
      "numeroImovel",
      "complemento",
      "areaTerreno",
      "areaConstruida",
      "tipoDeUso",
      "anoConstrucao"
    ]
    const iptu = this.#lote.iptu
    iptuFields.forEach(field => { register[field] = iptu[field] })
    if (this.#unidade) {
      register.contribuinte = this.#unidade.contribuinte
      register.complemento = this.#unidade.complemento
    }
  }
  async #fillITBIsInfos({ register }) { // eslint-disable-line
    const transacoes = this.#itbis?.list?.[0]?.data?.map(transacao => {
      return {
        situacaoSql: transacao.situacaoSql,
        data: transacao.data,
        complemento: transacao.complemento,
        natureza: transacao.natureza,
        proporcao: transacao.proporcao,
        valorTransacionado: transacao.valorTransacionado,
        area: transacao.area
      }
    })
    register.transacoes = transacoes
  }
}

class NegociacoesMultilines extends multilines {
  constructor({ form, register, fields, name = 'negociacoes', Logic }) {
    super({ form, register, fields, name, Logic, originClass: NegociacoesMultilines })
  }
  fillInfos({ register }) { // eslint-disable-line
    const loteRegister = register.lotes[0]
    if (loteRegister.valor !== "0,00" && loteRegister.precoPorMetroQuadrado !== "0,00") {
      this.addLine({ data: jsf.getToday(), valor: loteRegister.valor, valorporm2: loteRegister.precoPorMetroQuadrado })
    }
  }
}

class NegociacoesValorText extends FieldText {
  #form
  constructor({ form, register, fields, multiline = 'negociacoes', name = 'valor', Logic }) {
    super({ form, register, fields, multiline, name, Logic, originClass: NegociacoesValorText })
    this.#form = form
  }
  keyup({ vmodel }) { this.calculeNegociacaoValorporm2({ vmodel }) }
  calculeNegociacaoValorporm2({ vmodel }) {
    const area = jsf.numberIn(this.#form.register.area)
    const valorTotal = jsf.numberIn(vmodel.valor)
    const valorporm2 = valorTotal / area
    vmodel.valorporm2 = jsf.numberOut(valorporm2, 2)
  }
}

class NegociacoesValorporm2Text extends FieldText {
  #form
  constructor({ form, register, fields, multiline = 'negociacoes', name = 'valorporm2', Logic }) {
    super({ form, register, fields, multiline, name, Logic, originClass: NegociacoesValorporm2Text })
    this.#form = form
  }
  keyup({ vmodel }) { this.calculeNegociacaoValorTotal({ vmodel }) }
  calculeNegociacaoValorTotal({ vmodel }) {
    const area = jsf.numberIn(this.#form.register.area)
    const valorporm2 = jsf.numberIn(vmodel.valorporm2)
    const valorTotal = area * valorporm2
    vmodel.valor = jsf.numberOut(valorTotal, 2)
  }
}

export class ImovelFieldMap extends FieldMap { // Class to personalise terreno FieldMap
  #ref
  #form
  #lotes
  #register
  #fields
  #poligono
  #Logic
  #dataId
  constructor({ ref, form, register, fields, name = 'poligono', div, Logic, dataId }) {
    super({
      ref,
      form,
      register,
      fields,
      name,
      field: jsf.getJSONObject({ fields, name: 'poligono' }),
      div,
      Logic,
      originClass: ImovelFieldMap
    })
    this.#ref = ref
    this.#form = form
    this.#register = register
    this.#fields = fields
    this.#Logic = Logic
    this.#dataId = dataId
  }
  async centerMap() { super.setMapCenter({ address: 'São Paulo - SP', zoom: 9 }) }
  async setInFieldMap() {
    window.showOverlay()
    this.#lotes = new LotesDM({})
    await this.#lotes
      .form
      .readRegisterById({
        _id: this.#form.register.lotes?.[0].lote,
        project: { poligono: 1 }
      })
    this.#form.poligono.value = this.#lotes.form.register.poligono
    const _objects = await this.#lotes.getMapObject({ optionsType: 'saved' })
    const objects = _objects.map(object => object.mapObjectParts).flat()
    super.insertObjects({ objects })
    super.fitBounds({ objects, adjustZoom: - 1 })
    this.#calculeAreaAndPerimetro()
    window.hideOverlay()
  }
  async insertImovelOfRegister() {
    super.clear()
    const getObjects = async () => {
      const imoveis = new ImoveisDM({ ref: this.#ref, Logic: this.#Logic })
      imoveis.form.register = this.#form.register
      const types = {
        point: 'AdvancedMarker',
        circle: 'Circle'
      }
      const type = this.#form.register.poligono?.type
      const plotConfig = type ? types[type] : 'Polygon'
      const objects = await imoveis.getMapObject({ optionsType: 'saved', plotConfig })
      return objects
    }
    const objects = await getObjects()
    super.insertObjects({ objects })
    super.fitBounds({ objects, adjustZoom: - 1 })
  }
  #calculeAreaAndPerimetro() { // eslint-disable-line
    let area = 0
    const isPolygon = (objeto) => typeof objeto.getPath == 'function'
    const hasOnlyOnePolygon = () => this.objects.lotes?.filter(objeto => isPolygon(objeto)).length == 1
    this.objects.lotes?.map(object => area += isPolygon(object) ? window.google.maps.geometry.spherical.computeArea(object.getPath()) : 0)
    const perimeterPolygon = this.objects.lotes?.find(object => isPolygon(object))
    const perimeter = perimeterPolygon ? window.google.maps.geometry.spherical.computeLength(perimeterPolygon.getPath()) : 0
    this.#form.register.poligonoArea = jsf.numberOut(area, 2) || ''
    if (this.objects.lotes?.length == 1 && hasOnlyOnePolygon()) { this.#form.register.poligonoPerimetro = jsf.numberOut(perimeter, 2) }
  }
}

class AnunciosMultilines extends multilines {
  constructor({ form, register, fields, name = 'anuncios', Logic }) {
    super({ form, register, fields, name, Logic, originClass: AnunciosMultilines })
  }
  clickAdd() {
    this.lines.getLast().data = jsf.getToday()
  }
}